'use client';
import { useIsMounted } from '@toss/react';
import { atom, type SetStateAction, useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { usePublicMetaFilter } from '@/module/public/hooks/usePublicMetaFilter';
import {
  normalizePublicGradeId,
  normalizePublicSchoolType,
} from '@/module/public/util/publicCategoryFilterValue';
import { toggleArrayValue } from '@/module/public/util/toggleArrayValue';
import { isAllGrades } from '@/views/build/util';

export interface PublicCategoryState {
  schoolType: string;
  grades: string[];
  semesters: string[];
  subject: string;
}

type PublicCategoryKeyObj = Partial<Record<keyof PublicCategoryState, boolean>>;

export const initialPublicCategoryState: PublicCategoryState = {
  schoolType: '',
  grades: [],
  semesters: [],
  subject: '',
};

const publicCategoryStorageAtom = atomWithStorage<PublicCategoryState>(
  'publicCategory',
  initialPublicCategoryState,
);

// atomWithStorage가 undefined를 반환하는 edge case 방어 (QUIZ-APP-66R)
const publicCategoryAtom = atom(
  get => get(publicCategoryStorageAtom) ?? initialPublicCategoryState,
  (_get, set, update: SetStateAction<PublicCategoryState>) =>
    set(publicCategoryStorageAtom, update),
);

// GnbHeader 등에서 상태만 읽을 때 사용
export const usePublicCategoryState = (): readonly [
  PublicCategoryState,
  (update: SetStateAction<PublicCategoryState>) => void,
] => {
  const [state, setState] = useAtom(publicCategoryAtom);
  return [state, setState] as const;
};

export const usePublicCategoryFilter = () => {
  const [state, setState] = useAtom(publicCategoryAtom);
  const isMounted = useIsMounted();
  const { resetPublisher } = usePublicMetaFilter();
  const normalizedState: PublicCategoryState = {
    ...state,
    schoolType: normalizePublicSchoolType(state.schoolType),
    grades: state.grades.map(normalizePublicGradeId),
  };

  const handleSelectedSchoolType = async (schoolType: string) => {
    setState(prev => ({
      ...prev,
      schoolType: normalizePublicSchoolType(schoolType),
      subject: '',
      semesters: [],
      grades: [],
    }));
    resetPublisher();
  };

  const handleSelectedGrades = async (gradeId: string) => {
    setState(prev => {
      const nextGrades = toggleArrayValue(
        prev.grades.map(normalizePublicGradeId),
        normalizePublicGradeId(gradeId),
      ).sort();
      const onlyAllGrades =
        nextGrades.length === 1 && isAllGrades(nextGrades[0]);

      return {
        ...prev,
        grades: nextGrades,
        subject: '',
        // 전학년만 남으면 학기 초기화 (전학년에는 학기가 없으므로)
        ...(onlyAllGrades && { semesters: [] }),
      };
    });
    resetPublisher();
  };

  const handleSelectedSemesters = (semesterId: string) => {
    setState(prev => ({
      ...prev,
      semesters: toggleArrayValue(prev.semesters, semesterId).sort(),
    }));
  };

  const handleSelectedSubject = (subjectId: string) => {
    setState(prev => ({
      ...prev,
      subject: subjectId,
    }));
  };

  const handleCategory = (newCategory: Partial<PublicCategoryState>) => {
    setState(prev => ({
      ...prev,
      ...newCategory,
    }));
  };

  const removeAll = (excludesType: PublicCategoryKeyObj) => {
    setState(prev => ({
      ...prev,
      ...(!excludesType.schoolType && { schoolType: '' }),
      ...(!excludesType?.grades && { grades: [] }),
      ...(!excludesType?.semesters && { semesters: [] }),
      ...(!excludesType?.subject && { subject: '' }),
    }));
  };
  // 기존과 동일한 인터페이스 반환
  return {
    // 상태값들
    selectedSchoolTypeName: normalizedState.schoolType,
    selectedGrades: normalizedState.grades,
    selectedSemesters: state.semesters,
    selectedSubject: state.subject,
    isSubSubjectSelected: state.subject.length === 4,
    removeAll,
    state: normalizedState,
    initialized: isMounted,

    // 액션들
    handleSelectedSchoolType,
    handleSelectedGrades,
    handleSelectedSemesters,
    handleSelectedSubject,
    handleCategory,
  };
};
